// FamilyRisk.js
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import './familyrisk.css';
import './familyrisk-light.css';
import './FamilyRiskSettings.css';
import { ensureFamilyRiskApiKey } from './WebserverUser';
import {
  contractMissing,
  contractSubmission,
  createServiceUpload,
  hasParameterContract,
  loadServiceCatalogue,
  loadServiceJobs,
  openJobDownload,
  serviceRequest,
  WebserverApiError,
} from '../shared/xomics-webserver-client';
import CentralApiGuide from '../shared/CentralApiGuide';
import WebserverDemoSection from '../shared/WebserverDemoSection';
import AnalysisModal from './AnalysisModal';
import FMRPDataTable from './FMRPDataTable';
import FMRPDatabase from './FMRPDatabase';

import logoDark from '../../FamilyRisk.png';
import logoLight from '../../FamilyRisk_Dark.png';
import baseLogo from '../../base_logo.png';
import baseLogoLight from '../../base_logo_light.png';

const THEME_STORAGE_KEY = 'fmrp-theme-preference';
const SERVICE_CODE = 'familyrisk';

// Apply theme immediately on script load to prevent flash
(function applyStoredThemeImmediately() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  let theme = 'dark';
  if (stored === 'light') {
    theme = 'light';
  } else if (stored === 'system') {
    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    theme = prefersDark ? 'dark' : 'light';
  }
  const root = document.documentElement;
  root.classList.remove('fmrp-theme-light', 'fmrp-theme-dark');
  root.classList.add(theme === 'light' ? 'fmrp-theme-light' : 'fmrp-theme-dark');
})();

function FamilyRisk() {
  const [apiKey, setApiKey] = useState('');
  const [theme, setTheme] = useState('dark');
  const [activeTab, setActiveTab] = useState('home');

  // Jobs state
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isLoadingJobs, setIsLoadingJobs] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Analyses: the pipelines the XOmics catalogue maps to FamilyRisk, each with
  // its declared files, parameters, patient facts and report types.
  const [analysisTypes, setAnalysisTypes] = useState([]);
  const [isLoadingAnalyses, setIsLoadingAnalyses] = useState(true);
  const [analysesError, setAnalysesError] = useState('');
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [contractValues, setContractValues] = useState({ parameters: {}, facts: {} });
  const [selectedFiles, setSelectedFiles] = useState({});
  const [sampleCode, setSampleCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});

  // Polling ref
  const pollIntervalRef = useRef(null);

  // Initialize API key and fetch data
  useEffect(() => {
    const init = async () => {
      const key = await ensureFamilyRiskApiKey();
      setApiKey(key);

      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      if (stored) setTheme(stored);
    };
    init();

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Fetch analyses when API key is ready
  useEffect(() => {
    if (apiKey) {
      fetchAnalyses();
      fetchJobs();
      startJobPolling();
    }
  }, [apiKey]);

  // The analyses are the pipelines in the service catalogue, in mapping order.
  const fetchAnalyses = async () => {
    setIsLoadingAnalyses(true);
    try {
      const catalogue = await loadServiceCatalogue(SERVICE_CODE, apiKey);
      const pipelines = Array.isArray(catalogue.pipelines) ? catalogue.pipelines : [];
      setAnalysisTypes([...pipelines].sort((left, right) => (
        Number(left.display_order) - Number(right.display_order) || Number(left.id) - Number(right.id)
      )));
      setAnalysesError('');
    } catch (err) {
      console.error('Failed to fetch analyses:', err);
      setAnalysesError(err.message || 'The analyses could not be loaded');
    } finally {
      setIsLoadingAnalyses(false);
    }
  };

  // Fetch jobs (user jobs + demo jobs)
  const fetchJobs = useCallback(async () => {
    try {
      if (!apiKey) return;
      const response = await loadServiceJobs(SERVICE_CODE, apiKey, { limit: 50 });
      setJobs(response.jobs.map((job) => ({
        ...job,
        // The pipeline mode XOmics records on the job decides how its result
        // files are grouped.
        analysis_type: job.mode || job.pipeline,
      })));
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
    } finally {
      setIsLoadingJobs(false);
    }
  }, [apiKey]);

  // Poll for job updates
  const startJobPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
    pollIntervalRef.current = setInterval(() => {
      fetchJobs();
    }, 5000);
  };

  const handleThemeToggle = (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem(THEME_STORAGE_KEY, newTheme);

    const root = document.documentElement;
    root.classList.remove('fmrp-theme-light', 'fmrp-theme-dark');
    root.classList.add(newTheme === 'light' ? 'fmrp-theme-light' : 'fmrp-theme-dark');
  };

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.sample_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.job_id?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || job.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleSelectAnalysis = (analysis) => {
    setSelectedAnalysis(analysis);
    setSampleCode(analysis.sample_codes?.length === 1 ? analysis.sample_codes[0] : '');
    setContractValues({ parameters: {}, facts: {} });
    setSelectedFiles({});
  };

  const handleContinue = () => {
    if (!selectedAnalysis) return;
    setShowModal(true);
  };

  // Files are held by the input position the pipeline declares.
  const handleFileChange = (position, file) => {
    setSelectedFiles(prev => ({ ...prev, [position]: file }));
  };

  const selectedFileEntries = () => {
    const entries = [];
    for (const input of selectedAnalysis?.inputs || []) {
      const value = selectedFiles[input.position];
      const files = Array.isArray(value) ? value : value ? [value] : [];
      files.forEach((file, index) => entries.push({
        file,
        slotPosition: Number(input.position),
        label: index === 0 ? String(input.position) : `${input.position}_${index + 1}`,
      }));
    }
    return entries;
  };

  // Submit job
  const handleSubmit = async (sampleName) => {
    if (!selectedAnalysis || !sampleCode) return;
    if (!hasParameterContract(selectedAnalysis)) {
      alert('The parameter declaration for this analysis is not available from the API yet.');
      return;
    }
    const missing = contractMissing(selectedAnalysis, contractValues);
    if (missing.length) {
      alert(`Please fill in: ${missing.join(', ')}`);
      return;
    }

    setIsSubmitting(true);
    setUploadProgress({});

    try {
      const { patientParams, pipelineParams } = contractSubmission(selectedAnalysis, contractValues);
      await createServiceUpload({
        serviceCode: SERVICE_CODE,
        token: apiKey,
        pipeline: selectedAnalysis,
        sampleCode,
        selectedFiles: selectedFileEntries(),
        patientParams,
        pipelineParams,
        displayName: sampleName || `Analysis_${Date.now()}`,
        metadata: {
          mode: selectedAnalysis.mode,
          ...(patientParams || {}),
        },
        onProgress: ({ label, fraction }) => setUploadProgress((previous) => ({
          ...previous,
          [label]: Math.round(fraction * 100),
        })),
      });

      // Success - refresh jobs and close modal
      await fetchJobs();
      setShowModal(false);
      setSelectedAnalysis(null);
      setSampleCode('');
      setContractValues({ parameters: {}, facts: {} });
      setSelectedFiles({});

    } catch (err) {
      console.error('Submit error:', err);
      alert(err.message);
    } finally {
      setIsSubmitting(false);
      setUploadProgress({});
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  // Download results
  const handleDownload = async (job) => {
    try {
      await openJobDownload(SERVICE_CODE, apiKey, job);
    } catch (err) {
      console.error('Download error:', err);
      alert(err.message);
    }
  };

  // Helper to find the analysis (pipeline) for a job's mode
  const getJobAnalysis = (analysisType) => (
    analysisTypes.find((analysis) => analysis.mode === analysisType) || null
  );

  const getJobIconClass = () => 'dna';

  // Helper to get display name for a job's analysis
  const getJobAnalysisName = (analysisType) => {
    const analysis = getJobAnalysis(analysisType);
    return analysis?.display_name || analysisType;
  };

  // Helper to get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'running':
      case 'processing':
        return 'fa-spinner fa-spin';
      case 'completed':
        return 'fa-check';
      case 'failed':
        return 'fa-times';
      case 'queued':
        return 'fa-clock';
      case 'pending_upload':
        return 'fa-upload';
      default:
        return 'fa-circle';
    }
  };

  // Job info modal state
  const [showJobInfo, setShowJobInfo] = useState(null);

  // File browser modal state
  const [fileBrowserJob, setFileBrowserJob] = useState(null);
  const [fileBrowserFiles, setFileBrowserFiles] = useState([]);
  const [fileBrowserLoading, setFileBrowserLoading] = useState(false);
  const [fileBrowserPreview, setFileBrowserPreview] = useState(null); // { name, content, type }
  const [fileBrowserProgress, setFileBrowserProgress] = useState(0); // 0-100
  const [fileBrowserActiveFile, setFileBrowserActiveFile] = useState(null);
  const [reportLanguage, setReportLanguage] = useState('');
  const [reportType, setReportType] = useState('');
  const [reportChoices, setReportChoices] = useState({ reportTypes: [], languages: [] });
  const [reportChoicesLoading, setReportChoicesLoading] = useState(false);
  const [reportError, setReportError] = useState('');
  const [reportMode, setReportMode] = useState(false);
  const [reportAnalysis, setReportAnalysis] = useState('');
  const [reportRecommendation, setReportRecommendation] = useState('');
  const [reportReferences, setReportReferences] = useState('');
  const [reportPdfUrl, setReportPdfUrl] = useState(null);
  const [reportStatus, setReportStatus] = useState(null);
  const reportPollRef = useRef(null);

  const signedOutput = (job, file) => serviceRequest(
    SERVICE_CODE,
    apiKey,
    `/uploads/${encodeURIComponent(job.upload_uuid)}`
      + `/jobs/${encodeURIComponent(job.job_uuid)}`
      + `/outputs/${encodeURIComponent(file.outputId)}/download`,
  );

  const reportPath = (job, suffix = '') => {
    if (!job?.upload_uuid || !job?.job_uuid) {
      throw new Error('The report requires an exact upload and job reference');
    }
    return `/uploads/${encodeURIComponent(job.upload_uuid)}`
      + `/jobs/${encodeURIComponent(job.job_uuid)}/reports${suffix}`;
  };

  const downloadSingleFile = async (job, file) => {
    try {
      const signed = await signedOutput(job, file);
      window.open(signed.downloadUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  const loadFilePreview = async (job, file) => {
    setFileBrowserActiveFile(file.name);
    setFileBrowserPreview(null);
    setFileBrowserProgress(0);

    if (!file.previewable) {
      setFileBrowserPreview({ ...file, type: 'binary' });
      return;
    }

    try {
      const signed = await signedOutput(job, file);
      const resp = await fetch(signed.downloadUrl);
      if (!resp.ok) throw new Error(`Preview failed (${resp.status})`);
      const total = parseInt(resp.headers.get('content-length') || '0', 10);
      const reader = resp.body.getReader();
      const chunks = [];
      let loaded = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        loaded += value.length;
        if (total > 0) {
          setFileBrowserProgress(Math.min(Math.round((loaded / total) * 100), 99));
        } else {
          setFileBrowserProgress(prev => Math.min(prev + 5, 95));
        }
      }

      setFileBrowserProgress(100);
      const blob = new Blob(chunks);

      // Decompress .gz files
      let text;
      if (file.name.endsWith('.gz')) {
        const ds = new DecompressionStream('gzip');
        const decompressed = blob.stream().pipeThrough(ds);
        const decompressedBlob = await new Response(decompressed).blob();
        text = await decompressedBlob.text();
      } else {
        text = await blob.text();
      }

      const baseName = file.name.replace(/\.gz$/, '');
      const isHtml = baseName.endsWith('.html') || (resp.headers.get('content-type') || '').includes('html');
      const isJson = baseName.endsWith('.json');
      const isTsv = baseName.endsWith('.tsv') || baseName.endsWith('.csv') || baseName.endsWith('.txt');

      if (isJson) {
        try { text = JSON.stringify(JSON.parse(text), null, 2); } catch {}
      }

      // Detect tab/comma-separated data — DataTable expects TSV
      if (isTsv && text) {
        const lines = text.trim().split('\n').filter(l => l.trim());
        const sep = lines[0].includes('\t') ? '\t' : lines[0].includes(',') ? ',' : null;
        if (sep && lines.length > 1) {
          const tsvData = sep === '\t' ? text : lines.map(l => l.split(',').join('\t')).join('\n');
          setFileBrowserPreview({ ...file, type: 'table', content: tsvData });
          return;
        }
      }

      setFileBrowserPreview({ ...file, content: text, type: isHtml ? 'html' : 'text' });
    } catch (err) {
      console.error('Preview error:', err);
      setFileBrowserPreview({ ...file, content: 'Failed to load preview.', type: 'text' });
    }
  };

  const openFileBrowser = async (job) => {
    setFileBrowserJob(job);
    setFileBrowserFiles([]);
    setFileBrowserPreview(null);
    setFileBrowserActiveFile(null);
    setFileBrowserProgress(0);
    setFileBrowserLoading(true);
    try {
      const data = await serviceRequest(
        SERVICE_CODE,
        apiKey,
        `/uploads/${encodeURIComponent(job.upload_uuid)}`
          + `/jobs/${encodeURIComponent(job.job_uuid)}/outputs`,
      );
      const files = (data.outputs || []).map((output) => ({
        outputId: output.id,
        name: output.filename,
        size: output.bytes,
        previewable: /\.(html?|json|txt|tsv|csv)(\.gz)?$/i.test(output.filename),
      }));
      setFileBrowserFiles(files);
      // Auto-load first file from the categorized list
      const grouped = categorizeFiles(job.analysis_type, files);
      const firstFile = grouped.length > 0 && grouped[0].files.length > 0
        ? grouped[0].files[0] : null;
      if (firstFile) {
        loadFilePreview(job, firstFile);
      }
    } catch (err) {
      console.error('Failed to fetch job files:', err);
    } finally {
      setFileBrowserLoading(false);
    }
  };

  const fetchReportPdf = async (job, reportUuid) => {
    try {
      const signed = await serviceRequest(
        SERVICE_CODE,
        apiKey,
        reportPath(job, `/${encodeURIComponent(reportUuid)}/download`),
      );
      if (!signed.downloadUrl) throw new Error('The report download URL is missing');
      return `${signed.downloadUrl}#navpanes=0`;
    } catch (err) {
      console.error('[Report] Error fetching PDF:', err);
      setReportError(err.message || 'The report could not be downloaded');
      return null;
    }
  };

  const stopReportPolling = () => {
    if (reportPollRef.current) {
      clearInterval(reportPollRef.current);
      reportPollRef.current = null;
    }
  };

  const readLatestReport = async (job) => {
    const data = await serviceRequest(SERVICE_CODE, apiKey, reportPath(job, '/latest'));
    const report = data.report;
    if (!report?.uuid || !report.status) throw new Error('The report status response is incomplete');
    setReportStatus(report.status);
    setReportError(report.errorMessage || '');
    if (report.status === 'done') {
      stopReportPolling();
      const url = await fetchReportPdf(job, report.uuid);
      if (url) setReportPdfUrl(url);
    } else if (report.status === 'failed') {
      stopReportPolling();
      setReportPdfUrl(null);
    }
    return report;
  };

  const startReportPolling = (job) => {
    stopReportPolling();
    const poll = async () => {
      try {
        await readLatestReport(job);
      } catch (err) {
        console.error('[Report] Poll error:', err);
        setReportError(err.message || 'The report status could not be read');
        stopReportPolling();
      }
    };
    void poll();
    reportPollRef.current = setInterval(poll, 3000);
  };

  const checkExistingReport = async (job) => {
    try {
      const report = await readLatestReport(job);
      if (report.status === 'queued' || report.status === 'processing') startReportPolling(job);
    } catch (err) {
      if (err instanceof WebserverApiError && err.status === 404) {
        setReportStatus(null);
        setReportPdfUrl(null);
        return;
      }
      console.error('[Report] Existing report read failed:', err);
      setReportError(err.message || 'The existing report could not be read');
    }
  };

  const loadReportChoices = async (job) => {
    setReportChoicesLoading(true);
    setReportError('');
    try {
      const choices = await serviceRequest(SERVICE_CODE, apiKey, reportPath(job, '/options'));
      const reportTypes = Array.isArray(choices.reportTypes) ? choices.reportTypes : [];
      const languages = Array.isArray(choices.languages) ? choices.languages : [];
      setReportChoices({ reportTypes, languages });
      setReportType(reportTypes[0]?.code || '');
      setReportLanguage(
        languages.find((language) => language.code === 'en')?.code || languages[0]?.code || '',
      );
      if (reportTypes.length === 0) {
        setReportError('No active report type is configured for this pipeline.');
      } else if (languages.length === 0) {
        setReportError('No active report language is configured.');
      }
    } catch (err) {
      console.error('[Report] Options read failed:', err);
      setReportChoices({ reportTypes: [], languages: [] });
      setReportType('');
      setReportLanguage('');
      setReportError(err.message || 'Report options could not be loaded');
    } finally {
      setReportChoicesLoading(false);
    }
  };

  const submitReport = async () => {
    if (!fileBrowserJob || !reportType || !reportLanguage) return;
    const suppliedDate = String(fileBrowserJob.date_of_birth || '').trim();
    const patientDate = /^\d{4}-\d{2}-\d{2}$/.test(suppliedDate) ? suppliedDate : null;
    const suppliedGender = String(fileBrowserJob.gender || '').trim().toLowerCase();
    const patientGender = ['male', 'female', 'other'].includes(suppliedGender)
      ? suppliedGender : null;
    setReportStatus('submitting');
    setReportPdfUrl(null);
    setReportError('');
    try {
      const created = await serviceRequest(SERVICE_CODE, apiKey, reportPath(fileBrowserJob), {
        method: 'POST',
        body: JSON.stringify({
          content_type_code: reportType,
          language_code: reportLanguage,
          patient_name: fileBrowserJob.sample_name || null,
          patient_date_of_birth: patientDate,
          patient_gender: patientGender,
          options: {
            analysis: reportAnalysis,
            recommendation: reportRecommendation,
            references: reportReferences,
          },
        }),
      });
      setReportStatus(created.report?.status || 'queued');
      startReportPolling(fileBrowserJob);
    } catch (err) {
      console.error('[Report] Submit error:', err);
      setReportStatus('failed');
      setReportError(err.message || 'The report request failed');
    }
  };

  // When entering report mode, check for existing report
  useEffect(() => {
    if (reportMode && fileBrowserJob) {
      void loadReportChoices(fileBrowserJob);
      void checkExistingReport(fileBrowserJob);
    }
    if (!reportMode) {
      stopReportPolling();
    }
    return () => stopReportPolling();
  }, [reportMode, fileBrowserJob]);

  const closeFileBrowser = () => {
    setFileBrowserJob(null);
    setFileBrowserFiles([]);
    setFileBrowserPreview(null);
    setFileBrowserActiveFile(null);
    setFileBrowserProgress(0);
    setReportMode(false);
    setReportPdfUrl(null);
    setReportStatus(null);
    setReportChoices({ reportTypes: [], languages: [] });
    setReportType('');
    setReportLanguage('');
    setReportError('');
    stopReportPolling();
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (name) => {
    if (name.endsWith('.html')) return 'fa-file-code';
    if (name.endsWith('.tsv') || name.endsWith('.csv')) return 'fa-file-csv';
    if (name.endsWith('.vcf') || name.endsWith('.vcf.gz')) return 'fa-dna';
    if (name.endsWith('.json')) return 'fa-file-alt';
    if (name.endsWith('.pdf')) return 'fa-file-pdf';
    if (name.endsWith('.png') || name.endsWith('.jpg') || name.endsWith('.svg')) return 'fa-image';
    if (name.endsWith('.gz') || name.endsWith('.zip') || name.endsWith('.tar')) return 'fa-file-archive';
    return 'fa-file';
  };

  // Generate a friendly display name from a raw filename
  const getFileDisplayName = (name) => {
    const base = name.split('/').pop().split('\\').pop(); // strip path

    // PGx: extract sample ID from *_biallelic_nodup_pass.SAMPLE.report.html
    const pgxMatch = base.match(/_biallelic_nodup_pass\.(.+?)\.report\.html$/i);
    if (pgxMatch) return `PGx Report — ${pgxMatch[1]}`;
    if (/_biallelic_nodup_pass\.report\.html$/i.test(base)) return 'PGx Report';

    // PGS density plots
    if (/PGS_Znorm.*DensityPlot/i.test(base)) return 'PGS Density Plot';
    // PGS report
    if (/^report\.html$/i.test(base)) return 'PGS Report';
    // PGS scores
    if (/cohort_PGS/i.test(base) || /_pgs\./i.test(base)) return 'PGS Scores';

    // Known results files
    if (/^general_summary/i.test(base)) return 'General Summary';
    if (/^monogenic_positive_results/i.test(base)) return 'Monogenic Positive Results';
    if (/^carrier_status_results/i.test(base)) return 'Carrier Status Results';
    if (/^carrier_screening_result/i.test(base)) return 'Carrier Screening Results';
    if (/^family_information/i.test(base)) return 'Family Information';
    if (/^variant_summary/i.test(base)) return 'Variant Summary';
    if (/^Cohort_summary/i.test(base)) return 'Cohort Summary';
    if (/^plp_var_stat/i.test(base)) return 'PLP Variant Statistics';
    if (/^ontology_stat/i.test(base)) return 'Ontology Statistics';
    if (/^inhouse_allele_frequency/i.test(base)) return 'In-house Allele Frequency';

    // Pedigree plots: extract gene/variant info
    const pedMatch = base.match(/^.+?_(.+?)_pedigree_plot/i);
    if (pedMatch) return `Pedigree — ${pedMatch[1]}`;

    // CNV/SV
    if (/\.annotated\.tsv$/i.test(base)) return 'Annotated Variants';
    if (/compound_het\.tsv$/i.test(base)) return 'Compound Heterozygous';

    // Fallback: strip extension, replace _ and - with spaces
    return base.replace(/\.[^.]+$/, '').replace(/[_-]/g, ' ');
  };

  // Categorize files into grouped sections based on analysis type
  // File patterns from actual pipeline output:
  //   PGx  → PGx_Reports/*.report.html
  //   PGS  → PGS_Score(s)/, PRS_Scores/, PGx_Scores/ dirs, *_pgs.txt, cohort_PGS.txt, PGS_Znorm*DensityPlot.png
  //   Pedigree → *_pedigree_plot.png
  const categorizeFiles = (analysisType, files) => {
    const isPGx = f => /_biallelic_nodup_pass.*\.report\.html/i.test(f);
    const isPGS = f =>
      (/PGS_Score[s]?[/\\]|PRS_Score[s]?[/\\]|PGx_Score[s]?[/\\]/i.test(f) ||
      /_pgs\b/i.test(f) || /cohort_PGS/i.test(f) ||
      /PGS_Znorm/i.test(f) || /DensityPlot/i.test(f)) &&
      !/\.json(\.gz)?$/i.test(f) && !/\.ya?ml(\.gz)?$/i.test(f);
    const isPedigree = f => /pedigree/i.test(f);

    // Rules checked in order — first match wins. Catch-all .txt at the end
    // picks up the sample/family/cohort-specific variant details file.
    const typeRules = {
      carrier: [
        { title: 'Sample & Variant Summary', icon: 'fa-file-alt', match: f => /general_summary/i.test(f) },
        { title: 'Disease Carrier Status', icon: 'fa-dna', match: f => /monogenic_positive_results/i.test(f) },
        { title: 'Screen-Positive Results', icon: 'fa-user-shield', match: f => /carrier_status_results/i.test(f) },
        { title: 'Pharmacogenomics', icon: 'fa-pills', match: isPGx },
        { title: 'Polygenic Risk Scores', icon: 'fa-chart-line', match: isPGS },
        { title: 'Variant Details', icon: 'fa-baby', match: f => /\.txt$/i.test(f) },
      ],
      newborn: [
        { title: 'Sample & Variant Summary', icon: 'fa-file-alt', match: f => /general_summary/i.test(f) },
        { title: 'Disease Carrier Status', icon: 'fa-dna', match: f => /monogenic_positive_results/i.test(f) },
        { title: 'Screen-Positive Results', icon: 'fa-user-shield', match: f => /carrier_status_results/i.test(f) },
        { title: 'Pharmacogenomics', icon: 'fa-pills', match: isPGx },
        { title: 'Polygenic Risk Scores', icon: 'fa-chart-line', match: isPGS },
        { title: 'Variant Details', icon: 'fa-baby', match: f => /\.txt$/i.test(f) },
      ],
      carrier_family: [
        { title: 'Couple Details', icon: 'fa-people-arrows', match: f => /family_information/i.test(f) },
        { title: 'Variant Summary', icon: 'fa-list-alt', match: f => /variant_summary/i.test(f) },
        { title: 'Carrier Screening', icon: 'fa-user-shield', match: f => /carrier_screening_result/i.test(f) || /carrier_status_results/i.test(f) },
        { title: 'Monogenic Results', icon: 'fa-dna', match: f => /monogenic_positive_results/i.test(f) },
        { title: 'Pedigree', icon: 'fa-sitemap', match: isPedigree },
        { title: 'Pharmacogenomics', icon: 'fa-pills', match: isPGx },
        { title: 'Polygenic Risk Scores', icon: 'fa-chart-line', match: isPGS },
        { title: 'Variant Details', icon: 'fa-file-alt', match: f => /\.txt$/i.test(f) },
      ],
      newborn_family: [
        { title: 'Family Information', icon: 'fa-people-arrows', match: f => /family_information/i.test(f) },
        { title: 'Variant Summary', icon: 'fa-list-alt', match: f => /variant_summary/i.test(f) },
        { title: 'Screen-Positive Monogenic Diseases', icon: 'fa-dna', match: f => /monogenic_positive_results/i.test(f) },
        { title: 'Disease Carrier Status', icon: 'fa-user-shield', match: f => /carrier_status_results/i.test(f) },
        { title: 'Pedigree', icon: 'fa-sitemap', match: isPedigree },
        { title: 'Pharmacogenomics', icon: 'fa-pills', match: isPGx },
        { title: 'Polygenic Risk Scores', icon: 'fa-chart-line', match: isPGS },
        { title: 'Variant Details', icon: 'fa-baby', match: f => /\.txt$/i.test(f) },
      ],
      newborn_cohort: [
        { title: 'Cohort Summary', icon: 'fa-file-alt', match: f => /cohort_summary/i.test(f) },
        { title: 'Variant Statistics', icon: 'fa-chart-bar', match: f => /plp_var_stat/i.test(f) },
        { title: 'Disease Ontology Statistics', icon: 'fa-project-diagram', match: f => /ontology_stat/i.test(f) },
        { title: 'Allele Frequency', icon: 'fa-percentage', match: f => /inhouse_allele_frequency/i.test(f) },
        { title: 'Screen-Positive Monogenic Diseases', icon: 'fa-dna', match: f => /monogenic_positive_results/i.test(f) },
        { title: 'Disease Carrier Status', icon: 'fa-user-shield', match: f => /carrier_status_results/i.test(f) },
        { title: 'Pharmacogenomics', icon: 'fa-pills', match: isPGx },
        { title: 'Polygenic Risk Scores', icon: 'fa-chart-line', match: isPGS },
        { title: 'Variant Details', icon: 'fa-users', match: f => /\.txt$/i.test(f) },
      ],
      cnvsv: [
        //{ title: 'Annotated Variants', icon: 'fa-dna', match: f => /\.annotated\.tsv/i.test(f) },
        { title: 'Compound Heterozygous', icon: 'fa-layer-group', match: f => /compound_het/i.test(f) },
      ],
    };

    const rules = typeRules[analysisType] || [];

    // Deduplicate: if two files share the same base name (ignoring .gz), keep the largest
    const deduped = Object.values(
      files.reduce((map, file) => {
        const base = (file.name || '').replace(/\.gz$/, '');
        if (!map[base] || (file.size || 0) > (map[base].size || 0)) {
          map[base] = file;
        }
        return map;
      }, {})
    );

    const categories = {};
    const order = [];
    rules.forEach(r => {
      if (!categories[r.title]) {
        categories[r.title] = { title: r.title, icon: r.icon, files: [] };
        order.push(r.title);
      }
    });
    deduped.filter(file => !/\.png$/i.test(file.name || '')).forEach(file => {
      const name = file.name || '';
      const matched = rules.find(r => r.match(name));
      if (matched) {
        categories[matched.title].files.push(file);
      }
    });

    const result = [];
    order.forEach(title => {
      if (categories[title].files.length > 0) result.push(categories[title]);
    });
    return result;
  };

  const groupedFiles = useMemo(() => {
    if (!fileBrowserJob || fileBrowserFiles.length === 0) return [];
    return categorizeFiles(fileBrowserJob.analysis_type, fileBrowserFiles);
  }, [fileBrowserJob, fileBrowserFiles]);

  const logoSrc = theme === 'light' ? logoLight : logoDark;
  const baseLogoSrc = theme === 'light' ? baseLogoLight : baseLogo;
  const themeClass = theme === 'light' ? 'light-theme' : '';

  return (
    <div className={`FMRP-app-container ${themeClass}`}>
      {/* AppBar Header */}
      <header className="FMRP-header">
        <div className="FMRP-header-left">
          <a href="/" className="FMRP-logo-container">
            <img src={baseLogoSrc} alt="Home" className="FMRP-logo" />
          </a>
          <nav className="FMRP-nav-tabs">
            <button
              className={`FMRP-nav-tab ${activeTab === 'home' ? 'active' : ''}`}
              onClick={() => setActiveTab('home')}
            >
              Home
            </button>
            <button
              className={`FMRP-nav-tab ${activeTab === 'api' ? 'active' : ''}`}
              onClick={() => setActiveTab('api')}
            >
              API
            </button>
            <button
              className={`FMRP-nav-tab ${activeTab === 'database' ? 'active' : ''}`}
              onClick={() => setActiveTab('database')}
            >
              Database
            </button>
            <button
              className={`FMRP-nav-tab ${activeTab === 'help' ? 'active' : ''}`}
              onClick={() => setActiveTab('help')}
            >
              Help
            </button>
            <button
              className={`FMRP-nav-tab ${activeTab === 'settings' ? 'active' : ''}`}
              onClick={() => setActiveTab('settings')}
            >
              Settings
            </button>
          </nav>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="FMRP-content-area">
        {activeTab === 'settings' ? (
          <div className="FMRP-settings-container">
            <div className="FMRP-settings-content">
              <h1>Settings</h1>

              <div className="FMRP-settings-section">
                <h2><i className="fas fa-palette"></i> Appearance</h2>
                <div className="FMRP-setting-item">
                  <div className="FMRP-setting-info">
                    <h3>Theme</h3>
                    <p>Choose your preferred color scheme</p>
                  </div>
                  <div className="FMRP-theme-options">
                    <button
                      className={`FMRP-theme-btn-setting ${theme === 'dark' ? 'active' : ''}`}
                      onClick={() => handleThemeToggle('dark')}
                    >
                      <i className="fas fa-moon"></i> Dark
                    </button>
                    <button
                      className={`FMRP-theme-btn-setting ${theme === 'light' ? 'active' : ''}`}
                      onClick={() => handleThemeToggle('light')}
                    >
                      <i className="fas fa-sun"></i> Light
                    </button>
                  </div>
                </div>
              </div>

              <div className="FMRP-settings-section">
                <h2><i className="fas fa-info-circle"></i> About</h2>
                <div className="FMRP-about-info-content">
                  <div className="FMRP-about-item">
                    <span className="FMRP-about-label">Application</span>
                    <span className="FMRP-about-value">Family Risk</span>
                  </div>
                  <div className="FMRP-about-item">
                    <span className="FMRP-about-label">Version</span>
                    <span className="FMRP-about-value">1.0.0</span>
                  </div>
                  <div className="FMRP-about-item">
                    <span className="FMRP-about-label">Account ID</span>
                    <span className="FMRP-about-value">{apiKey || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : activeTab === 'help' ? (
          <div className="FMRP-help-page">
            <div className="FMRP-help-container">
              <div className="FMRP-help-header">
                <h1>Help & Support</h1>
                <p>Find guides, resources, and support for FamilyRisk</p>
              </div>

              <div className="FMRP-help-grid">
                <a href="https://xinmengliao.github.io/PhenomePortal-FamilyRisk/Home/" target="_blank" rel="noopener noreferrer" className="FMRP-help-card" style={{ textDecoration: 'none', color: 'inherit' }}>
                  <div className="FMRP-help-card-icon">
                    <i className="fas fa-book"></i>
                  </div>
                  <div className="FMRP-help-card-content">
                    <h3>Documentation</h3>
                    <p>Comprehensive guides and reference materials for FamilyRisk analysis tools</p>
                    <span className="FMRP-help-link">
                      <i className="fas fa-arrow-right"></i>
                      View Documentation
                    </span>
                  </div>
                </a>

                <div className="FMRP-help-card disabled">
                  <div className="FMRP-help-card-icon">
                    <i className="fas fa-graduation-cap"></i>
                  </div>
                  <div className="FMRP-help-card-content">
                    <h3>Tutorials</h3>
                    <p>Step-by-step guides to help you get started with FamilyRisk analysis tools</p>
                    <span className="FMRP-help-link">
                      <i className="fas fa-clock"></i>
                      Coming Soon
                    </span>
                  </div>
                </div>

                <div className="FMRP-help-card">
                  <div className="FMRP-help-card-icon">
                    <i className="fas fa-code"></i>
                  </div>
                  <div className="FMRP-help-card-content">
                    <h3>API Integration</h3>
                    <p>Technical documentation for integrating FamilyRisk into your workflow</p>
                    <button className="FMRP-help-link" onClick={() => setActiveTab('api')}>
                      <i className="fas fa-arrow-right"></i>
                      View API Docs
                    </button>
                  </div>
                </div>

                <div className="FMRP-help-card">
                  <div className="FMRP-help-card-icon">
                    <i className="fas fa-envelope"></i>
                  </div>
                  <div className="FMRP-help-card-content">
                    <h3>Contact Support</h3>
                    <p>Get help from our team for technical issues, questions, or feedback</p>
                    <a href="mailto:hello@phenomelongevity.com" className="FMRP-help-link">
                      <i className="fas fa-paper-plane"></i>
                      hello@phenomelongevity.com
                    </a>
                  </div>
                </div>
              </div>

              <div className="FMRP-help-faq">
                <h2>Frequently Asked Questions</h2>
                <div className="FMRP-faq-list">
                  <div className="FMRP-faq-item">
                    <h4><i className="fas fa-chevron-right"></i> What file formats are supported?</h4>
                    <p>FamilyRisk accepts VCF and VCF.GZ files for genetic variant data. Some analyses also support custom gene-disease lists in TSV format.</p>
                  </div>
                  <div className="FMRP-faq-item">
                    <h4><i className="fas fa-chevron-right"></i> How long does an analysis take?</h4>
                    <p>Analysis time varies by type and file size. Most individual analyses complete within 30-90 minutes. Cohort analyses may take longer.</p>
                  </div>
                  <div className="FMRP-faq-item">
                    <h4><i className="fas fa-chevron-right"></i> Is my data secure?</h4>
                    <p>Yes. All uploads are encrypted in transit and at rest. Data is processed in isolated environments and automatically deleted after analysis completion.</p>
                  </div>
                  <div className="FMRP-faq-item">
                    <h4><i className="fas fa-chevron-right"></i> Can I use the API programmatically?</h4>
                    <p>Yes. FamilyRisk provides a REST API for automated workflows. Check the API tab for endpoints, authentication, and code examples.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : activeTab === 'api' ? (
          <CentralApiGuide
            serviceCode={SERVICE_CODE}
            serviceName="FamilyRisk"
            token={apiKey}
          />
        ) : activeTab === 'database' ? (
          <FMRPDatabase />
        ) : (
        <div className="FMRP-home">
          {/* Banner Header */}
          <header className="FMRP-home-header">
            <div className="FMRP-welcome-container">
              <div className="FMRP-welcome-logo-wrapper">
                <img src={logoSrc} alt="FamilyRisk Logo" className="FMRP-welcome-logo" />
              </div>
              <div className="FMRP-welcome-text">
                <h1>Welcome to Family Risk</h1>
                <p>Genetic screening and carrier analysis platform for families</p>
              </div>
            </div>
          </header>

          {/* Content Grid */}
          <div className="FMRP-home-content">
            {/* Left Column - Jobs */}
            <section className="FMRP-left-column">
              <div className="FMRP-section FMRP-uploads-section">
                <h2><i className="fas fa-tasks"></i>Analysis Jobs</h2>

                <div className="FMRP-jobs-filter">
                  <div className="FMRP-search-input">
                    <i className="fas fa-search"></i>
                    <input
                      type="text"
                      placeholder="Search jobs..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="all">All</option>
                    <option value="running">Running</option>
                    <option value="completed">Completed</option>
                    <option value="queued">Queued</option>
                    <option value="pending_upload">Pending</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>

                <div className="FMRP-jobs-list">
                  {isLoadingJobs ? (
                    <div className="FMRP-empty-state">
                      <i className="fas fa-spinner fa-spin"></i>
                      <span>Loading...</span>
                    </div>
                  ) : filteredJobs.length === 0 ? (
                    <div className="FMRP-empty-state">
                      <i className="fas fa-folder-open"></i>
                      <span>No jobs found</span>
                    </div>
                  ) : (
                    filteredJobs.map(job => (
                      <div
                        key={job.job_id}
                        className={`FMRP-job-item ${selectedJob?.job_id === job.job_id ? 'selected' : ''}`}
                        onClick={() => job.status === 'completed' ? openFileBrowser(job) : setSelectedJob(job)}
                      >
                        <button
                          className="FMRP-job-info-btn"
                          onClick={(e) => { e.stopPropagation(); setShowJobInfo(job); }}
                          title="View Details"
                        >
                          <i className="fas fa-info-circle"></i>
                        </button>
                        <div className="FMRP-job-icon">
                          <i className={`fas fa-${getJobIconClass(job.analysis_type)}`}></i>
                        </div>
                        <div className="FMRP-job-content">
                          <div className="FMRP-job-title">
                            {job.isDemo && <span className="FMRP-demo-badge">DEMO</span>}
                            {job.sample_name}
                          </div>
                          <div className="FMRP-job-subtitle">{getJobAnalysisName(job.analysis_type)}</div>
                        </div>
                        <div className="FMRP-job-status-area">
                          <span className={`FMRP-job-type-badge ${job.status}`}>
                            <i className={`fas ${getStatusIcon(job.status)}`}></i>
                            {job.status}
                          </span>
                          {job.status === 'completed' && (
                            <button
                              className="FMRP-job-action-btn"
                              onClick={(e) => { e.stopPropagation(); handleDownload(job); }}
                              title="Download"
                            >
                              <i className="fas fa-download"></i>
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </section>

            {/* Right Column - New Analysis */}
            <aside className="FMRP-right-column">
              <div className="FMRP-section FMRP-analysis-section">
                <h2><i className="fas fa-plus-circle"></i>New Analysis</h2>

                {isLoadingAnalyses ? (
                  <div className="FMRP-empty-state">
                    <i className="fas fa-spinner fa-spin"></i>
                    <span>Loading analyses...</span>
                  </div>
                ) : (
                  <>
                    {analysesError && (
                      <div className="FMRP-empty-state">
                        <i className="fas fa-exclamation-triangle"></i>
                        <span>{analysesError}</span>
                      </div>
                    )}
                    <div className="FMRP-category-section">
                      <div className="FMRP-options-grid">
                        {analysisTypes.map((analysis) => (
                          <div
                            key={analysis.id}
                            className={`FMRP-option-card ${selectedAnalysis?.id === analysis.id ? 'active' : ''}`}
                            onClick={() => handleSelectAnalysis(analysis)}
                          >
                            <div className="FMRP-option-icon">
                              <i className="fas fa-dna"></i>
                            </div>
                            <div className="FMRP-option-text">
                              <h4>{analysis.display_name}</h4>
                              <p>{analysis.description}</p>
                              {analysis.est_duration_minutes ? (
                                <span>About {analysis.est_duration_minutes} min once it starts running</span>
                              ) : null}
                            </div>
                            {selectedAnalysis?.id === analysis.id && (
                              <div className="FMRP-option-check">
                                <i className="fas fa-check"></i>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Continue Button */}
                    {selectedAnalysis && (
                      <div className="FMRP-start-section">
                        <button className="FMRP-start-btn" onClick={handleContinue}>
                          <i className="fas fa-arrow-right"></i>
                          Continue
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
              <WebserverDemoSection serviceCode={SERVICE_CODE} />
            </aside>
          </div>
        </div>
        )}
      </main>

      {/* API Key Badge */}
      <div className="FMRP-cookie-icon">
        <i className="fas fa-cookie"></i>
        <span className="FMRP-tooltip">{`ID: ${apiKey?.slice(0, 8)}...`}</span>
      </div>

      {/* Analysis Configuration Modal */}
      <AnalysisModal
        isOpen={showModal}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
        pipeline={selectedAnalysis}
        contractValues={contractValues}
        onContractChange={setContractValues}
        selectedFiles={selectedFiles}
        onFileChange={handleFileChange}
        isSubmitting={isSubmitting}
        uploadProgress={uploadProgress}
        analysisLabel={selectedAnalysis?.display_name || ''}
        sampleCodes={selectedAnalysis?.sample_codes || []}
        sampleCode={sampleCode}
        onSampleCodeChange={setSampleCode}
      />

      {/* File Browser Modal */}
      {fileBrowserJob && (
        <div className="FMRP-modal-overlay" onClick={closeFileBrowser}>
          <div className="FMRP-filebrowser-modal" onClick={(e) => e.stopPropagation()}>

            {/* LEFT SIDEBAR */}
            <div className="FMRP-filebrowser-sidebar">
              {/* Job info section */}
              <div className="FMRP-filebrowser-jobinfo">
                <div className="FMRP-filebrowser-jobinfo-header">
                  <i className={`fas fa-${getJobIconClass(fileBrowserJob.analysis_type)}`}></i>
                  <span>{fileBrowserJob.sample_name}</span>
                  <button className="FMRP-modal-close" onClick={closeFileBrowser}>
                    <i className="fas fa-times"></i>
                  </button>
                </div>
                <div className="FMRP-filebrowser-jobinfo-rows">
                  <div className="FMRP-filebrowser-jobinfo-row">
                    <span className="FMRP-filebrowser-jobinfo-label">Analysis</span>
                    <span className="FMRP-filebrowser-jobinfo-value">{getJobAnalysisName(fileBrowserJob.analysis_type)}</span>
                  </div>
                  <div className="FMRP-filebrowser-jobinfo-row">
                    <span className="FMRP-filebrowser-jobinfo-label">Status</span>
                    <span className="FMRP-filebrowser-jobinfo-value">
                      <span className={`FMRP-job-type-badge ${fileBrowserJob.status}`}>
                        <i className={`fas ${getStatusIcon(fileBrowserJob.status)}`}></i>
                        {fileBrowserJob.status}
                      </span>
                    </span>
                  </div>
                  {fileBrowserJob.created_at && (
                    <div className="FMRP-filebrowser-jobinfo-row">
                      <span className="FMRP-filebrowser-jobinfo-label">Created</span>
                      <span className="FMRP-filebrowser-jobinfo-value">{new Date(fileBrowserJob.created_at).toLocaleDateString()}</span>
                    </div>
                  )}
                  {fileBrowserJob.completed_at && (
                    <div className="FMRP-filebrowser-jobinfo-row">
                      <span className="FMRP-filebrowser-jobinfo-label">Completed</span>
                      <span className="FMRP-filebrowser-jobinfo-value">{new Date(fileBrowserJob.completed_at).toLocaleDateString()}</span>
                    </div>
                  )}
                  <div className="FMRP-filebrowser-jobinfo-row">
                    <span className="FMRP-filebrowser-jobinfo-label">Job ID</span>
                    <span className="FMRP-filebrowser-jobinfo-value FMRP-filebrowser-jobid">{fileBrowserJob.job_id}</span>
                  </div>
                </div>
              </div>

              {/* Files list — grouped by category */}
              <div className="FMRP-filebrowser-files-section">
                <div className="FMRP-filebrowser-files-title">Files</div>
                <div className="FMRP-filebrowser-list">
                  {fileBrowserLoading ? (
                    <div className="FMRP-filebrowser-loading">
                      <i className="fas fa-spinner fa-spin"></i>
                    </div>
                  ) : fileBrowserFiles.length === 0 ? (
                    <div className="FMRP-filebrowser-loading">
                      <span>No files</span>
                    </div>
                  ) : (
                    groupedFiles.map((group) => (
                      <div className="FMRP-filebrowser-group" key={group.title}>
                        <div className="FMRP-filebrowser-group-header">
                          <i className={`fas ${group.icon}`}></i>
                          <span>{group.title}</span>
                          <span className="FMRP-filebrowser-group-count">{group.files.length}</span>
                        </div>
                        {group.files.map((file, idx) => (
                          <div
                            key={idx}
                            className={`FMRP-filebrowser-file${fileBrowserActiveFile === file.name ? ' active' : ''}`}
                            onClick={() => { if (reportMode) setReportMode(false); loadFilePreview(fileBrowserJob, file); }}
                          >
                            <i className={`fas ${getFileIcon(file.name)}`}></i>
                            <div className="FMRP-filebrowser-file-info">
                              <span className="FMRP-filebrowser-file-title">{getFileDisplayName(file.name)}</span>
                              <span className="FMRP-filebrowser-file-meta">{file.name} &middot; {formatFileSize(file.size)}</span>
                            </div>
                            {!file.previewable && <i className="fas fa-download FMRP-filebrowser-file-dl" title="Download only"></i>}
                          </div>
                        ))}
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Footer actions */}
              <div className="FMRP-filebrowser-sidebar-footer">
                <button
                  className="FMRP-filebrowser-download-all"
                  onClick={() => setReportMode(true)}
                  title="Clinical report"
                >
                  <i className="fas fa-file-medical"></i>
                </button>
                <button
                  className="FMRP-filebrowser-download-all"
                  onClick={() => handleDownload(fileBrowserJob)}
                  title="Download All"
                >
                  <i className="fas fa-download"></i>
                </button>
              </div>
            </div>

            {/* RIGHT PANEL */}
            {reportMode ? (
              <div className="FMRP-filebrowser-report-view">
                {/* Left half — text inputs */}
                <div className="FMRP-report-view-left">
                  <label className="FMRP-report-view-label">Report type</label>
                  <select
                    className="FMRP-report-view-select"
                    value={reportType}
                    onChange={(e) => setReportType(e.target.value)}
                    disabled={reportChoicesLoading || reportChoices.reportTypes.length === 0}
                  >
                    {reportChoices.reportTypes.map((type) => (
                      <option key={type.code} value={type.code}>{type.displayName}</option>
                    ))}
                  </select>
                  <label className="FMRP-report-view-label">Analysis</label>
                  <span className="FMRP-report-view-subtitle">Clinical findings and interpretation of results</span>
                  <textarea
                    className="FMRP-report-view-textarea"
                    value={reportAnalysis}
                    onChange={(e) => setReportAnalysis(e.target.value)}
                    placeholder="Enter analysis..."
                  />
                  <label className="FMRP-report-view-label">Recommendation</label>
                  <span className="FMRP-report-view-subtitle">Suggested actions and follow-up steps</span>
                  <textarea
                    className="FMRP-report-view-textarea"
                    value={reportRecommendation}
                    onChange={(e) => setReportRecommendation(e.target.value)}
                    placeholder="Enter recommendation..."
                  />
                  <label className="FMRP-report-view-label">References</label>
                  <span className="FMRP-report-view-subtitle">Supporting literature and citations, comma separated</span>
                  <textarea
                    className="FMRP-report-view-textarea"
                    value={reportReferences}
                    onChange={(e) => setReportReferences(e.target.value)}
                    placeholder="Enter references..."
                  />
                  <label className="FMRP-report-view-label">Language</label>
                  <select
                    className="FMRP-report-view-select"
                    value={reportLanguage}
                    onChange={(e) => setReportLanguage(e.target.value)}
                    disabled={reportChoicesLoading || reportChoices.languages.length === 0}
                  >
                    {reportChoices.languages.map((language) => (
                      <option key={language.code} value={language.code}>
                        {language.nativeName || language.name}
                      </option>
                    ))}
                  </select>
                  {reportError && (
                    <span className="FMRP-report-view-subtitle">{reportError}</span>
                  )}
                  <button
                    className="FMRP-report-view-continue"
                    disabled={reportChoicesLoading || !reportType || !reportLanguage
                      || ['submitting', 'queued', 'processing'].includes(reportStatus)}
                    onClick={submitReport}
                  >
                    <i className="fas fa-play"></i>
                    {reportStatus === 'submitting'
                      ? ' Submitting...'
                      : ['queued', 'processing'].includes(reportStatus)
                        ? ' Generating...'
                        : ' Generate report'}
                  </button>
                </div>
                {/* Right half — report preview */}
                <div className="FMRP-report-view-right">
                  {reportPdfUrl ? (
                    <iframe
                      src={reportPdfUrl}
                      title="Report PDF"
                    />
                  ) : ['queued', 'processing', 'submitting'].includes(reportStatus) ? (
                    <div className="FMRP-filebrowser-empty">
                      <i className="fas fa-spinner fa-spin"></i>
                      <span>{reportStatus === 'submitting' ? 'Submitting report...' : 'Generating report...'}</span>
                    </div>
                  ) : reportStatus === 'failed' ? (
                    <div className="FMRP-filebrowser-empty">
                      <i className="fas fa-exclamation-triangle"></i>
                      <span>Report generation failed</span>
                    </div>
                  ) : (
                    <div className="FMRP-filebrowser-empty">
                      <i className="fas fa-file-alt"></i>
                      <span>No report generated yet</span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="FMRP-filebrowser-preview">
                {/* Progress bar */}
                {fileBrowserActiveFile && !fileBrowserPreview && (
                  <div className="FMRP-filebrowser-progressbar-wrap">
                    <div className="FMRP-filebrowser-progressbar">
                      <div
                        className="FMRP-filebrowser-progressbar-fill"
                        style={{ width: `${fileBrowserProgress}%` }}
                      />
                    </div>
                    <span className="FMRP-filebrowser-progress-label">Loading {fileBrowserActiveFile}... {fileBrowserProgress}%</span>
                  </div>
                )}

                {fileBrowserPreview ? (
                  fileBrowserPreview.type === 'html' ? (
                    <iframe
                      className="FMRP-filebrowser-iframe"
                      srcDoc={(() => {
                        let html = fileBrowserPreview.content;
                        html = html.replace(/<script[\s\S]*?<\/script>/gi, '');
                        html = html.replace(/(<a\s+(?=[^>]*href=["']https?:\/\/))/gi, '$1target="_blank" rel="noopener noreferrer" ');
                        const navScript = '<script>document.addEventListener("click",function(e){var a=e.target.closest("a");if(!a)return;var h=a.getAttribute("href");if(!h)return;if(h.startsWith("#")){e.preventDefault();var t=document.getElementById(h.slice(1))||document.querySelector(h);if(t)t.scrollIntoView({behavior:"smooth"});}else if(!h.startsWith("http")){e.preventDefault();}});<\/script>';
                        return html + navScript;
                      })()}
                      title={fileBrowserPreview.name}
                      sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
                    />
                  ) : fileBrowserPreview.type === 'table' ? (
                    <FMRPDataTable
                      apiKey={apiKey}
                      data={fileBrowserPreview.content}
                      title={getFileDisplayName(fileBrowserPreview.name)}
                      job={fileBrowserJob}
                      output={fileBrowserPreview}
                      pedigreeFiles={fileBrowserFiles.filter((file) => /pedigree.*\.png$/i.test(file.name))}
                    />
                  ) : fileBrowserPreview.type === 'binary' ? (
                    <div className="FMRP-filebrowser-empty">
                      <i className={`fas ${getFileIcon(fileBrowserPreview.name)}`}></i>
                      <span>{fileBrowserPreview.name}</span>
                      <span style={{ fontSize: '1.2vh', opacity: 0.6 }}>{formatFileSize(fileBrowserPreview.size)}</span>
                      <button className="FMRP-filebrowser-download-all" onClick={() => downloadSingleFile(fileBrowserJob, fileBrowserPreview)}>
                        <i className="fas fa-download"></i> Download
                      </button>
                    </div>
                  ) : (
                    <pre className="FMRP-filebrowser-text">{fileBrowserPreview.content}</pre>
                  )
                ) : !fileBrowserActiveFile && (
                  <div className="FMRP-filebrowser-empty">
                    <i className="fas fa-file-alt"></i>
                    <span>Select a file to preview</span>
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      )}

      {/* Job Info Modal */}
      {showJobInfo && (
        <div className="FMRP-modal-overlay" onClick={() => setShowJobInfo(null)}>
          <div className="FMRP-job-info-modal" onClick={(e) => e.stopPropagation()}>
            <div className="FMRP-job-info-header">
              <h2><i className={`fas fa-${getJobIconClass(showJobInfo.analysis_type)}`}></i> Job Details</h2>
              <button className="FMRP-modal-close" onClick={() => setShowJobInfo(null)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="FMRP-job-info-content">
              <div className="FMRP-job-info-row">
                <span className="FMRP-job-info-label">Job ID</span>
                <span className="FMRP-job-info-value">{showJobInfo.job_id}</span>
              </div>
              <div className="FMRP-job-info-row">
                <span className="FMRP-job-info-label">Sample Name</span>
                <span className="FMRP-job-info-value">{showJobInfo.sample_name}</span>
              </div>
              <div className="FMRP-job-info-row">
                <span className="FMRP-job-info-label">Analysis Type</span>
                <span className="FMRP-job-info-value">{showJobInfo.analysis_type}</span>
              </div>
              <div className="FMRP-job-info-row">
                <span className="FMRP-job-info-label">Status</span>
                <span className="FMRP-job-info-value">
                  <span className={`FMRP-job-type-badge ${showJobInfo.status}`}>
                    <i className={`fas ${getStatusIcon(showJobInfo.status)}`}></i>
                    {showJobInfo.status}
                  </span>
                </span>
              </div>
              {showJobInfo.created_at && (
                <div className="FMRP-job-info-row">
                  <span className="FMRP-job-info-label">Created</span>
                  <span className="FMRP-job-info-value">{new Date(showJobInfo.created_at).toLocaleString()}</span>
                </div>
              )}
              {showJobInfo.started_at && (
                <div className="FMRP-job-info-row">
                  <span className="FMRP-job-info-label">Started</span>
                  <span className="FMRP-job-info-value">{new Date(showJobInfo.started_at).toLocaleString()}</span>
                </div>
              )}
              {showJobInfo.completed_at && (
                <div className="FMRP-job-info-row">
                  <span className="FMRP-job-info-label">Completed</span>
                  <span className="FMRP-job-info-value">{new Date(showJobInfo.completed_at).toLocaleString()}</span>
                </div>
              )}
              {showJobInfo.error && (
                <div className="FMRP-job-info-row">
                  <span className="FMRP-job-info-label">Error</span>
                  <span className="FMRP-job-info-value FMRP-job-error">{showJobInfo.error}</span>
                </div>
              )}
              {showJobInfo.params && Object.keys(showJobInfo.params).length > 0 && (
                <div className="FMRP-job-info-section">
                  <h3>Parameters</h3>
                  {Object.entries(showJobInfo.params).map(([key, value]) => (
                    <div className="FMRP-job-info-row" key={key}>
                      <span className="FMRP-job-info-label">{key}</span>
                      <span className="FMRP-job-info-value">{String(value)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FamilyRisk;
